import React from 'react'
import OfferAirportSelectOption from './OfferAirportSelectOption'

export type GroupedAirport = App.AirportLocation | {
  multiAirport: App.AirportLocation
  childAirports: Array<App.AirportLocation>
}

export function groupAirportsByCity(airports: Array<App.AirportLocation>): Array<GroupedAirport> {
  // Identify multi-airports
  const multiAirports = airports.filter(airport => airport.isMultiAirport,
  )

  // Create a map of cityCode to multi-airport
  const multiAirportMap = new Map<string, App.AirportLocation>()
  multiAirports.forEach(airport => {
    if (airport.cityCode) {
      multiAirportMap.set(airport.cityCode, airport)
    }
  })

  // Group child airports by cityCode (excluding multi-airports)
  const cityGroups = new Map<string, Array<App.AirportLocation>>()
  airports.forEach(airport => {
    if (airport.cityCode && !airport.isMultiAirport) {
      if (!cityGroups.has(airport.cityCode)) {
        cityGroups.set(airport.cityCode, [])
      }
      cityGroups.get(airport.cityCode)!.push(airport)
    }
  })

  const result: Array<GroupedAirport> = []
  const processedAirports = new Set<App.AirportLocation>()

  // Process multi-airports with their children
  multiAirports.forEach(multiAirport => {
    const cityCode = multiAirport.cityCode
    const childAirports = cityCode ? cityGroups.get(cityCode) || [] : []

    if (childAirports.length > 0) {
      // Has children, create grouped object
      result.push({
        multiAirport,
        childAirports,
      })
      processedAirports.add(multiAirport)
      childAirports.forEach(child => processedAirports.add(child))
    } else {
      // No children, treat as individual airport
      result.push(multiAirport)
      processedAirports.add(multiAirport)
    }
  })

  // Add remaining individual airports that don't belong to any group
  airports.forEach(airport => {
    if (!processedAirports.has(airport)) {
      result.push(airport)
    }
  })

  return result
}

interface Props {
  airports: Array<App.Airport>
  selectedAirport?: App.Airport
  handleItemClick: (airport: App.AirportLocation) => void
  withRadioButtons?: boolean
}

function OfferAirportOptionsGroupedList({ airports, selectedAirport, handleItemClick, withRadioButtons }: Props) {
  const groupedAirports = groupAirportsByCity(airports.map(airport => ({
    ...airport,
    airportCode: airport.code,
    airportName: airport.name,
  })))

  return (
    <>
      {groupedAirports.map((item, index) => {
        // Check if it's a grouped airport or individual airport
        if ('multiAirport' in item) {
          // It's a grouped airport with children
          const { multiAirport, childAirports } = item
          return (
            <React.Fragment key={`group-${multiAirport.airportCode}-${index}`}>
              <OfferAirportSelectOption
                airport={multiAirport}
                onClick={handleItemClick}
                selected={multiAirport.airportCode === selectedAirport?.code}
                data-testid={`airport-select-${multiAirport.airportCode}`}
                withRadioButton={withRadioButtons}
              />
              {childAirports.map((childAirport) => (
                <OfferAirportSelectOption
                  key={`child-${childAirport.airportCode}`}
                  airport={childAirport}
                  onClick={handleItemClick}
                  selected={childAirport.airportCode === selectedAirport?.code}
                  data-testid={`airport-select-${childAirport.airportCode}`}
                  withRadioButton={withRadioButtons}
                  indented
                />
              ))}
            </React.Fragment>
          )
        } else {
          // It's an individual airport
          return (
            <OfferAirportSelectOption
              key={`individual-${item.airportCode}-${index}`}
              airport={item}
              onClick={handleItemClick}
              selected={item.airportCode === selectedAirport?.code}
              data-testid={`airport-select-${item.airportCode}`}
              withRadioButton={withRadioButtons}
            />
          )
        }
      })}
    </>
  )
}

export default OfferAirportOptionsGroupedList
